import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  interactive?: boolean;
  showValue?: boolean;
  showCount?: boolean;
  count?: number;
  precision?: number; // 0.5 for half stars, 1 for whole stars
  onRatingChange?: (rating: number) => void;
  className?: string;
  disabled?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'md',
  interactive = false,
  showValue = false,
  showCount = false,
  count,
  precision = 1,
  onRatingChange,
  className = '',
  disabled = false
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Size mappings
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const starSize = sizeClasses[size];
  const textSize = textSizeClasses[size];

  // Calculate the displayed rating (hover takes priority if interactive)
  const displayRating = interactive && isHovering ? hoverRating : rating;

  // Handle mouse events for interactive ratings
  const handleMouseEnter = (starValue: number) => {
    if (!interactive || disabled) return;
    setHoverRating(starValue);
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    if (!interactive || disabled) return;
    setIsHovering(false);
  };

  const handleClick = (starValue: number) => {
    if (!interactive || disabled || !onRatingChange) return;
    onRatingChange(starValue);
  };

  // Generate star array
  const stars = Array.from({ length: maxRating }, (_, index) => {
    const starValue = index + 1;
    const fillPercentage = Math.min(Math.max(displayRating - index, 0), 1);
    
    // Determine star state
    let starState: 'empty' | 'partial' | 'full' = 'empty';
    if (fillPercentage >= 1) {
      starState = 'full';
    } else if (fillPercentage > 0) {
      starState = 'partial';
    }

    return (
      <div key={index} className="relative">
        {/* Background star (empty) */}
        <Star
          className={`${starSize} text-gray-300 ${
            interactive && !disabled ? 'cursor-pointer' : 'cursor-default'
          }`}
          onMouseEnter={() => handleMouseEnter(starValue)}
          onClick={() => handleClick(starValue)}
        />
        
        {/* Foreground star (filled) */}
        {starState !== 'empty' && (
          <Star
            className={`${starSize} absolute top-0 left-0 pointer-events-none ${
              isHovering && interactive
                ? 'text-yellow-400'
                : 'text-yellow-400'
            }`}
            fill="currentColor"
            style={{
              clipPath: starState === 'partial' 
                ? `inset(0 ${100 - fillPercentage * 100}% 0 0)`
                : 'none'
            }}
          />
        )}
      </div>
    );
  });

  return (
    <div 
      className={`flex items-center gap-1 ${className}`}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center">
        {stars}
      </div>
      
      {showValue && (
        <span className={`text-gray-700 font-medium ml-2 ${textSize}`}>
          {displayRating.toFixed(precision === 0.5 ? 1 : 0)}
        </span>
      )}
      
      {showCount && count !== undefined && (
        <span className={`text-gray-500 ml-1 ${textSize}`}>
          ({count.toLocaleString()})
        </span>
      )}

      {interactive && !disabled && (
        <span className={`text-gray-400 ml-2 ${textSize}`}>
          {isHovering ? 'Click to rate' : 'Rate this'}
        </span>
      )}
    </div>
  );
};

export default StarRating; 